import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ChevronRight, ChevronDown, ExternalLink, Search, Info, Lightbulb, Check, List } from 'lucide-react';
import { cn } from "../lib/utils";
import { useMediaQuery } from "../hooks/use-media-query";

// Animation variants with reduced motion support
const createAnimationVariants = (prefersReducedMotion, isMobile) => {
  // If user prefers reduced motion, use minimal animations
  if (prefersReducedMotion) {
    return {
      panelVariants: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.2 } }
      },
      contentFadeVariants: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } }
      },
      searchHighlightVariants: {
        hidden: { backgroundColor: "rgba(59, 130, 246, 0)" },
        visible: { backgroundColor: "rgba(59, 130, 246, 0.1)", transition: { duration: 0.3 } }
      },
      overlayVariants: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.2 } }
      }
    };
  }
  
  // Full animations for users who don't prefer reduced motion
  return {
    panelVariants: {
      hidden: isMobile ? { 
        opacity: 0,
        scale: 0.95,
        y: 10
      } : { 
        opacity: 0,
        scale: 0.98,
        y: 10,
        x: 20
      },
      visible: isMobile ? {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          type: "spring",
          damping: 30,
          stiffness: 350,
          duration: 0.35
        }
      } : {
        opacity: 1,
        scale: 1,
        y: 0,
        x: 0,
        transition: {
          type: "spring",
          damping: 25,
          stiffness: 300,
          duration: 0.4
        }
      },
      exit: isMobile ? {
        opacity: 0,
        scale: 0.95,
        y: 10,
        transition: {
          duration: 0.25,
          ease: [0.32, 0.72, 0, 1]
        }
      } : {
        opacity: 0,
        scale: 0.98,
        y: 10,
        x: 20,
        transition: {
          duration: 0.25,
          ease: [0.32, 0.72, 0, 1]
        }
      }
    },
    contentFadeVariants: {
      hidden: { opacity: 0, y: 10 },
      visible: custom => ({
        opacity: 1,
        y: 0,
        transition: {
          delay: custom * 0.05,
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }
      })
    },
    searchHighlightVariants: {
      hidden: { backgroundColor: "rgba(59, 130, 246, 0)" },
      visible: { 
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        transition: {
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }
      }
    },
    overlayVariants: {
      hidden: { opacity: 0 },
      visible: { 
        opacity: 1,
        transition: { duration: 0.2 }
      },
      exit: { 
        opacity: 0,
        transition: { duration: 0.2 }
      }
    }
  };
};

/**
 * HelpPanel - A reusable, elegant help documentation panel component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is open
 * @param {Function} props.onClose - Function to close the panel
 * @param {string} props.title - Title of the help panel
 * @param {Object|string|React.ReactNode} props.content - Content to display (HTML string, React node, or object with sections)
 * @param {Array} props.sections - Optional sections for navigation
 * @param {string} props.position - Position of the panel ('right', 'left', 'center')
 * @param {string} props.size - Size of the panel ('small', 'medium', 'large')
 * @param {boolean} props.closeOnClickOutside - Whether to close the panel when clicking outside
 * @returns {React.ReactNode}
 */
const HelpPanel = ({
  isOpen,
  onClose,
  title = "Help & Information",
  content,
  sections = [],
  position = "right",
  size = "medium",
  closeOnClickOutside = true
}) => {
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isTocExpanded, setIsTocExpanded] = useState(false);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const contentRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const prefersReducedMotion = useReducedMotion();
  
  // Generate animation variants based on motion preference
  const {
    panelVariants,
    contentFadeVariants,
    searchHighlightVariants,
    overlayVariants
  } = createAnimationVariants(prefersReducedMotion, isMobile);
  
  // Determine effective panel position and size
  const effectivePosition = isMobile ? "bottom" : position;
  const effectiveSize = isMobile ? "full" : size;
  
  // Set the intro section as active ONLY when opening with no active section
  useEffect(() => {
    if (isOpen && sections && sections.length > 0 && !activeSection) {
      setActiveSection(sections[0].id);
      
      // Scroll to the first section when initially opening
      setTimeout(() => {
        const element = document.getElementById(sections[0].id);
        if (element) {
          element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [isOpen, sections, activeSection, prefersReducedMotion]);

  // Handle click outside
  useEffect(() => {
    if (!closeOnClickOutside) return;
    
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, closeOnClickOutside]);
  
  // Handle search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    
    // Simulate search with a small delay
    const timer = setTimeout(() => {
      const query = searchQuery.toLowerCase().trim();
      const results = [];
      
      // Search in sections
      if (sections && sections.length > 0) {
        sections.forEach(section => {
          if (section.title.toLowerCase().includes(query)) {
            results.push({
              type: 'section',
              id: section.id,
              title: section.title,
              match: 'title'
            });
          }
          
          // Search in section content
          if (section.content) {
            let contentText = '';
            if (typeof section.content === 'string') {
              contentText = section.content;
            } else if (typeof section.content === 'object') {
              contentText = JSON.stringify(section.content);
            }
            
            if (contentText.toLowerCase().includes(query)) {
              if (!results.some(r => r.id === section.id)) {
                results.push({
                  type: 'section',
                  id: section.id,
                  title: section.title,
                  match: 'content'
                });
              }
            }
          }
        });
      }
      
      // Search in overall content if it's a string
      if (typeof content === 'string' && content.toLowerCase().includes(query)) {
        results.push({
          type: 'content',
          match: 'content'
        });
      }
      
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, sections, content]);
  
  // Jump to a section and collapse TOC
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    // Collapse the TOC when a section is selected
    setIsTocExpanded(false);
    
    // Simple and elegant solution for scrolling to the section
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      const scrollContainer = panelRef.current.querySelector('.overflow-auto');
      
      if (element && scrollContainer) {
        const elementTop = element.offsetTop;
        const headerHeight = 78; // Match the top padding spacer
        
        scrollContainer.scrollTo({
          top: elementTop - headerHeight,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      }
    }, 100); // Short delay to let state update
  };
  
  // Get active section title
  const getActiveSectionTitle = () => {
    if (!activeSection || !sections || sections.length === 0) return 'Overview';
    const section = sections.find(s => s.id === activeSection);
    return section ? section.title : 'Overview';
  };
  
  // Parse HTML content and format it
  const renderContentFromHTML = (htmlContent) => {
    if (!htmlContent) return null;
    
    return (
      <div 
        className="prose prose-gray max-w-none"
        dangerouslySetInnerHTML={{ __html: htmlContent }} 
      />
    );
  };
  
  // Get panel width based on size
  const getPanelWidthClass = () => {
    if (isMobile) return "w-full max-w-full";
    
    switch (effectiveSize) {
      case 'small': return "w-full max-w-sm";
      case 'large': return "w-full max-w-2xl";
      case 'full': return "w-full max-w-5xl";
      case 'medium':
      default: return "w-full max-w-md";
    }
  };
  
  // Get panel position class
  const getPanelPositionStyles = () => {
    if (isMobile) {
      return {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        maxHeight: '85vh',
        width: 'calc(100% - 2rem)',
        maxWidth: '420px',
        borderRadius: '1.25rem'
      };
    }
    
    const baseStyles = {
      maxHeight: 'calc(100vh - 4rem)'
    };
    
    switch (effectivePosition) {
      case 'left':
        return {
          ...baseStyles,
          top: '2rem',
          left: '2rem',
          bottom: '2rem',
          right: 'auto'
        };
      case 'center':
        return {
          ...baseStyles,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bottom: 'auto',
          right: 'auto'
        };
      case 'right':
      default:
        return {
          ...baseStyles,
          top: '2rem',
          right: '2rem',
          bottom: '2rem',
          left: 'auto'
        };
    }
  };
  
  // Calculate section height class
  const getSectionHeightClass = () => {
    if (isMobile) return "max-h-[65vh]";
    return "max-h-[70vh]";
  };
  
  // Render the table of contents accordion
  const renderTableOfContents = () => {
    if (!sections || sections.length === 0) return null;
    
    // Create an ID for accessibility
    const tocId = "help-panel-toc";
    const tocButtonId = "help-panel-toc-button";
    
    return (
      <div className="pb-1">
        {/* Accordion header */}
        <motion.button
          id={tocButtonId}
          onClick={() => setIsTocExpanded(!isTocExpanded)}
          className="w-full flex items-center justify-between py-2 text-left text-gray-700 hover:text-gray-900 focus:outline-none transition-colors"
          whileHover={prefersReducedMotion ? {} : { scale: 1.01 }}
          whileTap={prefersReducedMotion ? {} : { scale: 0.99 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          aria-expanded={isTocExpanded}
          aria-controls={tocId}
        >
          <div className="flex items-center space-x-2">
            <List className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">
              {isTocExpanded ? "In this guide:" : `Current: ${getActiveSectionTitle()}`}
            </span>
          </div>
          <motion.div
            animate={{ rotate: isTocExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </motion.div>
        </motion.button>
        
        {/* Accordion content */}
        <AnimatePresence>
          {isTocExpanded && (
            <motion.div
              id={tocId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0, transition: { duration: 0.2 } }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
              className="overflow-hidden"
              role="region"
              aria-labelledby={tocButtonId}
            >
              <motion.ul className="space-y-1 mt-2 pt-2 border-t border-gray-100 max-h-[50vh] overflow-y-auto pb-1">
                {sections.map((section, index) => (
                  <motion.li 
                    key={section.id}
                    custom={index}
                    variants={contentFadeVariants}
                    initial="hidden"
                    animate="visible"
                    className="text-sm"
                  >
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "flex items-center w-full text-left py-1.5 px-2 rounded-md transition-colors",
                        activeSection === section.id
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                      aria-current={activeSection === section.id ? "true" : "false"}
                    >
                      <ChevronRight 
                        className={cn(
                          "mr-1.5 h-3 w-3 flex-shrink-0 transition-transform",
                          activeSection === section.id ? "text-gray-600 transform rotate-90" : "text-gray-400"
                        )}
                      />
                      <span className="truncate">{section.title}</span>
                    </button>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
  
  // Render search results
  const renderSearchResults = () => {
    if (!searchQuery.trim()) return null;
    
    if (isSearching) {
      return (
        <div className="py-8 text-center text-gray-500">
          <motion.div
            animate={prefersReducedMotion ? {} : { rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="mx-auto w-6 h-6 border-2 border-t-gray-400 border-gray-200 rounded-full mb-2"
          />
          <p>Searching...</p>
        </div>
      );
    }
    
    if (searchResults.length === 0) {
      return (
        <div className="py-8 text-center">
          <Info className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-gray-500">No results found for "{searchQuery}"</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
        </div>
      );
    }
    
    return (
      <div className="mt-4 mb-6">
        <h3 className="text-sm font-medium text-gray-500 mb-3">
          {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
        </h3>
        
        <ul className="space-y-2" role="listbox" aria-label="Search results">
          {searchResults.map((result, index) => (
            <motion.li 
              key={result.id || index}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: prefersReducedMotion ? 0 : index * 0.05, duration: 0.3 }}
              role="option"
            >
              <motion.button
                onClick={() => {
                  if (result.type === 'section') {
                    scrollToSection(result.id);
                  }
                  setSearchQuery('');
                  searchRef.current?.blur();
                }}
                className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                whileHover={prefersReducedMotion ? {} : { y: -1 }}
                whileTap={prefersReducedMotion ? {} : { y: 0 }}
              >
                <div className="flex items-start">
                  <div className="mr-2 mt-0.5 text-blue-500">
                    <Search className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{result.title}</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      Found in {result.match === 'title' ? 'section title' : 'content'}
                    </div>
                  </div>
                  <ChevronRight className="ml-auto h-5 w-5 text-gray-400" />
                </div>
              </motion.button>
            </motion.li>
          ))}
        </ul>
      </div>
    );
  };
  
  // Generate unique IDs for accessibility
  const panelId = "help-panel";
  const titleId = "help-panel-title";
  const searchId = "help-panel-search";
  const contentId = "help-panel-content";
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay (only for mobile or when position is center) */}
          {(isMobile || effectivePosition === 'center') && (
            <motion.div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998]"
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={closeOnClickOutside ? onClose : undefined}
              aria-hidden="true"
            />
          )}
          
          {/* Main help panel */}
          <motion.div
            ref={panelRef}
            className={cn(
              "fixed z-[9999] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden",
              getPanelWidthClass()
            )}
            style={getPanelPositionStyles()}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={contentId}
            id={panelId}
          >
            {/* Header */}
            <header className="flex items-center justify-between px-5 py-4 sm:p-5 border-b border-gray-100 bg-white">
              <motion.div 
                className="flex items-center"
                initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Info className="mr-2.5 h-5 w-5 text-gray-700" />
                <h2 id={titleId} className="text-lg font-medium text-gray-900 line-clamp-1">{title}</h2>
              </motion.div>
              
              <motion.button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none"
                aria-label="Close help panel"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </header>
            
            {/* Search bar */}
            <div className="px-5 pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchRef}
                  id={searchId}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search help..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:bg-white transition-colors"
                  aria-label="Search help content"
                />
                {searchQuery && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Content */}
            <div 
              id={contentId} 
              className="flex-1 flex flex-col relative overflow-hidden"
            >
              {searchQuery ? (
                <div className="p-4 sm:p-5 overflow-auto">
                  {renderSearchResults()}
                </div>
              ) : (
                <>
                  {/* Fixed Table of Contents - positioned absolutely at top, never scrolls */}
                  <div className="absolute top-0 left-0 right-0 z-30 px-5 pt-2 pb-1 bg-white border-b border-gray-100">
                    {renderTableOfContents()}
                  </div>
                  
                  {/* Proper content padding to account for fixed header */}
                  <div className="w-full pt-[78px]" aria-hidden="true"></div>
                  
                  {/* Scrollable content area - completely isolated from TOC with proper padding */}
                  <div className="flex-1 overflow-auto px-5 pb-4">
                    {sections && sections.length > 0 ? (
                      <div className="space-y-6 pt-1" role="region" aria-label="Help content sections">
                        {sections.map((section, index) => (
                          <motion.section
                            key={section.id}
                            id={section.id}
                            className="p-4 bg-white rounded-lg border border-gray-50 shadow-sm scroll-mt-24"
                            custom={index}
                            variants={contentFadeVariants}
                            initial="hidden"
                            animate="visible"
                            tabIndex="-1"
                          >
                            <h2 className="text-xl font-medium text-gray-900 mb-3">{section.title}</h2>
                            {typeof section.content === 'string' 
                              ? renderContentFromHTML(section.content)
                              : section.content
                            }
                          </motion.section>
                        ))}
                      </div>
                    ) : (
                      <div className="prose prose-gray max-w-none pt-1" role="region" aria-label="Help content">
                        {typeof content === 'string' ? renderContentFromHTML(content) : content}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Footer */}
            <footer className="px-5 py-4 sm:p-5 border-t border-gray-100 flex justify-between items-center">
              <div className="hidden sm:flex items-center text-sm text-gray-500">
                <Lightbulb className="h-4 w-4 mr-2 text-amber-500" />
                <span>Have a question? Click any section title above.</span>
              </div>
              
              <motion.button
                onClick={onClose}
                className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
              >
                Got it
              </motion.button>
            </footer>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HelpPanel; 